"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { IonContent, IonPage } from "@ionic/react"
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Container,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  TablePagination,
  Checkbox,
  TableFooter,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  TrendingDown,
  Inventory2,
  AttachMoney,
  FilterList,
  Clear,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import axios from "axios"
import { requestID, baseURL, config } from "../../../common/utils"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

interface InventoryItem {
  id: string
  dateCreated: string
  product_id: string
  productName: string
  description: string
  productCategory: string
  unit_price: string
  selling_price: string
  total_quantity_supplied: string
  total_quantity_consumed: string
  total_quantity_left: string
  bar_quantity_left: string
  bar_qty?: string
  user_id?: string
  name?: string;
  qty?: string;
  price?: string;
  category?: string;
  // New fields from JSON response
  CurrentStock?: number;
  OpeningBalance?: number;
  TotalPurchasedAfterDate?: string;
  TotalSoldAfterDate?: string;
  branch_id?: string;
  branch_measure?: string;
  business_id?: string;
  category_id?: string;
  grade_letter?: string;
  img_src?: string | null;
  instocks?: number;
  last_updated?: string;
  minSalePrice?: string;
  purchasePrice?: string;
  reorder_qty?: string;
  stockControlled?: string;
  totalQtyPosted?: number;
  totalQtySold?: number;
  type?: string;
  warehouse_measure?: string;
}

const Inventory: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem | null>(null)
  const [inventory, setInventory] = useState<InventoryItem[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(500)
  const [selected, setSelected] = useState<readonly string[]>([])
  const [startDate, setStartDate] = useState<string>("")

  // Helper functions
  const parseNumber = (val: any): number => {
    if (val === null || val === undefined || val === '') return 0
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ""))
    return isNaN(num) ? 0 : num
  }

  const formatCurrency = (amount: string | number): string => {
    const numAmount = typeof amount === "string" ? parseNumber(amount) : amount
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
  }

  const getStockStatus = (qtyLeft: number) => {
    if (qtyLeft === 0) return { label: "Out of Stock", color: "error" }
    if (qtyLeft < 10) return { label: "Low Stock", color: "warning" }
    return { label: "In Stock", color: "success" }
  }

  // Statistics calculations
  const getTotalValue = (): number => {
    return inventory.reduce((total, item) => {
      const qty = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      const price = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
      return total + (qty * price)
    }, 0)
  }

  const getTotalItems = (): number => {
    return inventory.reduce((total, item) => {
      return total + (item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0'))
    }, 0)
  }

  const getLowStockCount = (): number => {
    return inventory.filter((item) => {
      const qty = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      return qty > 0 && qty < 10
    }).length
  }

  const getOutOfStockCount = (): number => {
    return inventory.filter((item) => {
      const qty = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      return qty === 0
    }).length
  }

  // Footer calculations for current page
  const getCurrentPageTotals = () => {
    let totalQty = 0
    let totalValue = 0
    let totalOpeningBalance = 0
    let totalOpeningValue = 0
    let totalPurchasedValue = 0
    let totalSoldValue = 0

    paginatedInventory.forEach((item) => {
      const qty = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      const price = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
      const openingBalance = item.OpeningBalance || 0
      const totalPurchased = parseNumber(item.TotalPurchasedAfterDate || '0')
      const totalSold = parseNumber(item.TotalSoldAfterDate || '0')
      
      totalQty += qty
      totalValue += qty * price
      totalOpeningBalance += openingBalance
      totalOpeningValue += openingBalance * price
      totalPurchasedValue += totalPurchased * price
      totalSoldValue += totalSold * price
    })

    return { totalQty, totalValue, totalOpeningBalance, totalOpeningValue, totalPurchasedValue, totalSoldValue }
  }

  // Footer calculations for filtered inventory
  const getFilteredTotals = () => {
    let totalQty = 0
    let totalValue = 0
    let totalOpeningBalance = 0
    let totalOpeningValue = 0
    let totalPurchasedValue = 0
    let totalSoldValue = 0

    filteredInventory.forEach((item) => {
      const qty = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
      const price = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
      const openingBalance = item.OpeningBalance || 0
      const totalPurchased = parseNumber(item.TotalPurchasedAfterDate || '0')
      const totalSold = parseNumber(item.TotalSoldAfterDate || '0')
      
      totalQty += qty
      totalValue += qty * price
      totalOpeningBalance += openingBalance
      totalOpeningValue += openingBalance * price
      totalPurchasedValue += totalPurchased * price
      totalSoldValue += totalSold * price
    })

    return { totalQty, totalValue, totalOpeningBalance, totalOpeningValue, totalPurchasedValue, totalSoldValue }
  }

  const handleOpen = (item: InventoryItem) => {
    setSelectedInventory(item)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedInventory(null)
  }

  const fetchInventory = async (startDateParam = "") => {
    try {
      setLoading(true)
      const payload = { 
        requestID: requestID(),
        startDate: startDateParam,
      }
      const res = await axios.post(`${baseURL}warehouses/getProducts`, payload, config)
      console.log("API Response:", res.data)
      setInventory(res.data.data || [])
    } catch (error) {
      console.error("Error fetching products:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDateFilter = () => {
    setPage(0)
    fetchInventory(startDate)
  }

  const clearDateFilter = () => {
    setStartDate("")
    setPage(0)
    fetchInventory()
  }

  useEffect(() => {
    fetchInventory()
  }, [])

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = paginatedInventory.map((n) => n.product_id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: readonly string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }
    setSelected(newSelected)
  }

  const isSelected = (id: string) => selected.indexOf(id) !== -1

  const handleSubmitSelected = async () => {
    console.log("Submitting selected items:", selected)

    try {
      setLoading(true)
      const payload = { 
        requestID: requestID(),
        data: selected,
      }
      const res = await axios.post(`${baseURL}warehouses/deleteInventory`, payload, config)
      console.log(res)
      fetchInventory(startDate)
    } catch (error) {
      console.error("Error fetchInventory :", error)
    } finally {
      setLoading(false)
    }
  
    setSelected([])
  }

  const filteredInventory = inventory.filter((item) =>
    `${item.name || item.productName} ${item.description} ${item.dateCreated} ${item.category || item.productCategory} ${item.product_id}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  )

  const paginatedInventory = filteredInventory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const currentPageTotals = getCurrentPageTotals()
  const filteredTotals = getFilteredTotals()

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color="#1e293b"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <InventoryIcon />
                    Inventory Management
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Monitor and manage your product inventory levels
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchInventory(startDate)}
                    disabled={loading}
                    sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitSelected}
                    disabled={selected.length === 0}
                    sx={{ ml: 2 }}
                  >
                    Submit Selected ({selected.length})
                  </Button>
                </Box>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Stats Dashboard */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <Inventory2 sx={{ fontSize: 32, mb: 1, color: "#3b82f6" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {inventory?.length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Items
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <AttachMoney sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b" sx={{ fontSize: "1.5rem" }}>
                  {formatCurrency(getTotalValue())}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Value
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TrendingDown sx={{ fontSize: 32, mb: 1, color: "#f59e0b" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getLowStockCount()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Low Stock
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TrendingUp sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getOutOfStockCount()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Out of Stock
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Main Content */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Date Filter */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Search Inventory"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setPage(0)
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      label="Filter Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      startIcon={<FilterList />}
                      onClick={handleDateFilter}
                      disabled={loading}
                      sx={{ minWidth: 'auto' }}
                    >
                      Filter
                    </Button>
                    {startDate && (
                      <Button
                        variant="outlined"
                        startIcon={<Clear />}
                        onClick={clearDateFilter}
                        disabled={loading}
                        sx={{ minWidth: 'auto' }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Table */}
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="#64748b" sx={{ mt: 2 }}>
                    Loading inventory...
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              indeterminate={selected.length > 0 && selected.length < paginatedInventory.length}
                              checked={paginatedInventory.length > 0 && selected.length === paginatedInventory.length}
                              onChange={handleSelectAllClick}
                              inputProps={{
                                'aria-label': 'select all desserts',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Price</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Opening Balance</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Opening Balance Value</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Total Purchased</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Total Purchased Value</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Total Sold</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Total Sold Value</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Current Stock</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="center">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedInventory.length > 0 ? (
                          paginatedInventory.map((item) => {
                            const currentStock = item.CurrentStock || parseNumber(item.qty || item.total_quantity_left || '0')
                            const openingBalance = item.OpeningBalance || 0
                            const totalPurchased = parseNumber(item.TotalPurchasedAfterDate || '0')
                            const totalSold = parseNumber(item.TotalSoldAfterDate || '0')
                            const itemPrice = parseNumber(item.price || item.selling_price || item.minSalePrice || '0')
                            const openingBalanceValue = openingBalance * itemPrice
                            const totalPurchasedValue = totalPurchased * itemPrice
                            const totalSoldValue = totalSold * itemPrice
                            const itemValue = currentStock * itemPrice
                            
                            const stockStatus = getStockStatus(currentStock)
                            const isItemSelected = isSelected(item.product_id)
                            const labelId = `inventory-table-checkbox-${item.product_id}`

                            return (
                              <TableRow 
                                key={item.product_id} 
                                hover
                                onClick={(event) => handleClick(event, item.product_id)}
                                role="checkbox"
                                aria-checked={isItemSelected}
                                tabIndex={-1}
                                selected={isItemSelected}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    checked={isItemSelected}
                                    inputProps={{
                                      'aria-labelledby': labelId,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body1" fontWeight="500" color="#1e293b">
                                      {item.name || item.productName}
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                      ID: {item.product_id}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="#64748b">
                                      {item.last_updated ? formatDate(item.last_updated) : formatDate(item.dateCreated)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.category || item.productCategory || item.type}
                                    size="small"
                                    sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="600" color="#1e293b">
                                      {formatCurrency(itemPrice)}
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                      Value: {formatCurrency(itemValue)}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="500" color="#1e293b">
                                    {openingBalance.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="600" color="#10b981">
                                    {formatCurrency(openingBalanceValue)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="500" color="#10b981">
                                    +{totalPurchased.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="600" color="#10b981">
                                    {formatCurrency(totalPurchasedValue)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="500" color="#ef4444">
                                    -{totalSold.toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="600" color="#ef4444">
                                    {formatCurrency(totalSoldValue)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="600" color="#1e293b">
                                      {currentStock.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                      Closing Balance
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={stockStatus.label} 
                                    size="small" 
                                    color={stockStatus.color as any} 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); handleOpen(item); }}
                                    startIcon={<VisibilityIcon />}
                                    sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                                  >
                                    View
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                              <InventoryIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                              <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                                {searchTerm || startDate ? "No matching inventory found" : "No inventory items available"}
                              </Typography>
                              <Typography variant="body2" color="#94a3b8">
                                {searchTerm || startDate
                                  ? "Try adjusting your search terms or date filters"
                                  : "Add products to see them in inventory"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>

                      {/* Footer with Totals */}
                      {paginatedInventory.length > 0 && (
                        <TableFooter>
                          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                            <TableCell colSpan={4} align="right">
                              <Typography variant="subtitle1" fontWeight="600">
                                Page Totals:
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600">
                                {currentPageTotals.totalOpeningBalance.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(currentPageTotals.totalOpeningValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                +{paginatedInventory.reduce((sum, item) => sum + parseNumber(item.TotalPurchasedAfterDate || '0'), 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(currentPageTotals.totalPurchasedValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                -{paginatedInventory.reduce((sum, item) => sum + parseNumber(item.TotalSoldAfterDate || '0'), 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                {formatCurrency(currentPageTotals.totalSoldValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(currentPageTotals.totalValue)}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {currentPageTotals.totalQty.toLocaleString()} units
                              </Typography>
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                            <TableCell colSpan={4} align="right">
                              <Typography variant="subtitle1" fontWeight="600">
                                Filtered Totals ({filteredInventory.length} items):
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600">
                                {filteredTotals.totalOpeningBalance.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(filteredTotals.totalOpeningValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                +{filteredInventory.reduce((sum, item) => sum + parseNumber(item.TotalPurchasedAfterDate || '0'), 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(filteredTotals.totalPurchasedValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                -{filteredInventory.reduce((sum, item) => sum + parseNumber(item.TotalSoldAfterDate || '0'), 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#ef4444">
                                {formatCurrency(filteredTotals.totalSoldValue)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" fontWeight="600" color="#10b981">
                                {formatCurrency(filteredTotals.totalValue)}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {filteredTotals.totalQty.toLocaleString()} units
                              </Typography>
                            </TableCell>
                            <TableCell colSpan={2}></TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {filteredInventory.length > 0 && (
                    <TablePagination
                      component="div"
                      count={filteredInventory.length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(Number.parseInt(e.target.value, 10))
                        setPage(0)
                      }}
                      sx={{ borderTop: "1px solid #e2e8f0", mt: 2 }}
                    />
                  )}
                </>
              )}
            </CardContent>
          </StyledCard>

          {/* Details Dialog */}
          <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="600">
                  Product Details
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: "#64748b" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              {selectedInventory && (
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Basic Information
                    </Typography>
                    <Box sx={{ bgcolor: "#f8fafc", p: 2, borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Product Name
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.productName || selectedInventory.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Product ID
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.product_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Category
                          </Typography>
                          <Chip
                            label={selectedInventory.productCategory || selectedInventory.category || selectedInventory.type}
                            size="small"
                            sx={{ bgcolor: "#e2e8f0", color: "#475569" }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="#64748b">
                            Last Updated
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.last_updated ? formatDate(selectedInventory.last_updated) : formatDate(selectedInventory.dateCreated)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="#64748b">
                            Description
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {selectedInventory.description || "No description available"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Pricing Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Pricing Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dcfce7", border: "1px solid #bbf7d0" }}>
                          <Typography variant="body2" color="#166534">
                            Purchase Price
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#166534">
                            {formatCurrency(selectedInventory.purchasePrice || selectedInventory.unit_price)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dbeafe", border: "1px solid #bfdbfe" }}>
                          <Typography variant="body2" color="#1e40af">
                            Selling Price
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#1e40af">
                            {formatCurrency(selectedInventory.selling_price || selectedInventory.price || selectedInventory.minSalePrice)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Stock Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                      Stock Information {startDate && `(as of ${formatDate(startDate)})`}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f0f9ff", border: "1px solid #e0f2fe" }}>
                          <Typography variant="body2" color="#0369a1">
                            Opening Balance
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#0369a1">
                            {selectedInventory.OpeningBalance?.toLocaleString() || '0'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#dcfce7", border: "1px solid #bbf7d0" }}>
                          <Typography variant="body2" color="#166534">
                            Purchased
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#166534">
                            +{parseNumber(selectedInventory.TotalPurchasedAfterDate || '0').toLocaleString()}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#fef3c7", border: "1px solid #fde68a" }}>
                          <Typography variant="body2" color="#92400e">
                            Sold
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="#92400e">
                            -{parseNumber(selectedInventory.TotalSoldAfterDate || '0').toLocaleString()}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            bgcolor:
                              getStockStatus(selectedInventory.CurrentStock || 0).color === "error"
                                ? "#fee2e2"
                                : getStockStatus(selectedInventory.CurrentStock || 0).color === "warning"
                                  ? "#fef3c7"
                                  : "#dcfce7",
                            border: `1px solid ${
                              getStockStatus(selectedInventory.CurrentStock || 0).color === "error"
                                ? "#fecaca"
                                : getStockStatus(selectedInventory.CurrentStock || 0).color === "warning"
                                  ? "#fde68a"
                                  : "#bbf7d0"
                            }`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color={
                              getStockStatus(selectedInventory.CurrentStock || 0).color === "error"
                                ? "#991b1b"
                                : getStockStatus(selectedInventory.CurrentStock || 0).color === "warning"
                                  ? "#92400e"
                                  : "#166534"
                            }
                          >
                            Current Stock
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            color={
                              getStockStatus(selectedInventory.CurrentStock || 0).color === "error"
                                ? "#991b1b"
                                : getStockStatus(selectedInventory.CurrentStock || 0).color === "warning"
                                  ? "#92400e"
                                  : "#166534"
                            }
                          >
                            {selectedInventory.CurrentStock?.toLocaleString() || selectedInventory.total_quantity_left || selectedInventory.qty || '0'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={handleClose}
                variant="contained"
                sx={{
                  bgcolor: "#1e293b",
                  "&:hover": { bgcolor: "#334155" },
                  textTransform: "none",
                  px: 3,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </StyledDialog>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default Inventory